import { useEffect, useState } from "react";
import { Calendar, Clock, MapPin, User, Loader2 } from "lucide-react";
import { eventApi, humanDay } from "@/lib/eventHub";

/**
 * Text-based agenda that groups event_program items by day. Renders on the
 * Program tab (as a companion to the PDF booklets) AND embedded inline on the
 * Check-In screen so attendees can see the schedule while checking in.
 *
 * Props:
 *   compact?: boolean — smaller cards + no times headline (default false)
 *   day?: "2026-07-17" | "2026-07-18" — filter to a single day
 *   title?: string — override the section title
 */
export default function EventAgenda({ compact = false, day = null, title = "Event Agenda" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let m = true;
    (async () => {
      try {
        const { data } = await eventApi.program(day);
        if (m) setItems(data?.entries || data?.items || []);
      } catch { /* noop */ }
      if (m) setLoading(false);
    })();
  }, [day]);

  if (loading) {
    return (
      <div className={`${compact ? "py-4" : "py-8"} text-center text-emerald-800/40`} data-testid="agenda-loading">
        <Loader2 className="mx-auto animate-spin" />
      </div>
    );
  }
  if (items.length === 0) return null;

  // Group by day
  const byDay = items.reduce((acc, it) => {
    (acc[it.event_day] ||= []).push(it);
    return acc;
  }, {});
  const daySort = Object.keys(byDay).sort();

  return (
    <section data-testid="event-agenda" className={compact ? "" : "space-y-6"}>
      {!compact && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-[Outfit] font-bold text-fuchsia-700 flex items-center gap-1.5">
            <Calendar size={12} /> {title}
          </div>
          <p className="text-xs font-[Outfit] text-emerald-950/60 mt-1">Organized by day and time · America/New_York</p>
        </div>
      )}
      {daySort.map((d) => (
        <div key={d} data-testid={`agenda-day-${d}`} className={compact ? "mb-4" : ""}>
          <div className={`sticky top-0 -mx-1 px-1 py-1.5 ${compact ? "bg-white" : "bg-white/95 backdrop-blur"} z-10`}>
            <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 ${d === "2026-07-17" ? "bg-fuchsia-100 text-fuchsia-800" : "bg-emerald-100 text-emerald-800"}`}>
              <Calendar size={11} />
              <span className="text-[10px] tracking-[0.22em] uppercase font-[Outfit] font-bold">
                {humanDay(d)} · {d === "2026-07-17" ? "VIP Experience · 4:30–8:30 PM" : "Business Showers · 1:00–5:30 PM"}
              </span>
            </div>
          </div>
          <ol className="mt-2 space-y-2">
            {byDay[d].map((it) => (
              <li key={it.id} data-testid={`agenda-item-${it.id}`} className={`rounded-2xl border-2 border-emerald-100 bg-white ${compact ? "p-3" : "p-4"} flex items-start gap-3`}>
                <div className={`shrink-0 w-16 sm:w-20 ${compact ? "" : "text-right"}`}>
                  <div className="font-[Cormorant_Garamond] text-lg text-fuchsia-700 leading-none">{fmt12(it.start_time)}</div>
                  <div className="text-[9px] font-[Outfit] uppercase tracking-widest text-emerald-950/40 mt-0.5">–{fmt12(it.end_time)}</div>
                </div>
                <div className="flex-1 min-w-0 border-l border-emerald-100 pl-3">
                  <div className={`font-[Cormorant_Garamond] leading-tight text-emerald-950 ${compact ? "text-base" : "text-lg"}`}>{it.title}</div>
                  {it.description && !compact && (
                    <p className="text-[11px] font-[Outfit] text-emerald-950/70 mt-1 leading-relaxed">{it.description}</p>
                  )}
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] font-[Outfit] text-emerald-950/60">
                    {it.location && <span className="inline-flex items-center gap-0.5"><MapPin size={10} /> {it.location}</span>}
                    {it.speaker && <span className="inline-flex items-center gap-0.5"><User size={10} /> {it.speaker}</span>}
                    {it.sponsor && <span className="inline-flex items-center gap-0.5 rounded-full bg-fuchsia-50 border border-fuchsia-100 px-2 py-0.5 text-fuchsia-700 font-bold">{it.sponsor}</span>}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      ))}
    </section>
  );
}

function fmt12(t) {
  if (!t) return "";
  const [h, m] = t.split(":");
  let hh = parseInt(h, 10);
  const ampm = hh >= 12 ? "PM" : "AM";
  hh = hh % 12 || 12;
  return `${hh}:${m} ${ampm}`;
}
